
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlusCircle, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { AddFilmForm } from "./AddFilmForm";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FilmFormValues, filmSchema } from "@/types/inventory";
import { SortButton, SortOption } from "./SortButton";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";

interface FilmSearchAndAddProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onAddFilm: (values: FilmFormValues) => Promise<boolean>;
  sortOptions: SortOption[];
  activeSort: string | null;
  sortDirection: 'asc' | 'desc' | null;
  onSortChange: (value: string) => void;
  onDirectionChange: () => void;
  isAddDialogOpen?: boolean;
  setIsAddDialogOpen?: (isOpen: boolean) => void;
}

export function FilmSearchAndAdd({
  searchQuery,
  onSearchChange,
  onAddFilm,
  sortOptions,
  activeSort,
  sortDirection,
  onSortChange,
  onDirectionChange,
  isAddDialogOpen,
  setIsAddDialogOpen
}: FilmSearchAndAddProps) {
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  
  // Use external state if provided, otherwise use internal state
  const dialogOpen = isAddDialogOpen !== undefined ? isAddDialogOpen : internalDialogOpen;
  const setDialogOpen = setIsAddDialogOpen || setInternalDialogOpen;
  
  const form = useForm<FilmFormValues>({
    resolver: zodResolver(filmSchema),
    defaultValues: {
      film_name: "",
      film_type: "35mm",
      status: "fresh", // Changed from "unexposed" to "fresh"
      brand: "",
      iso: 400,
      quantity: 1,
      notes: "",
    },
  });

  const handleAddFilm = async (values: FilmFormValues) => {
    const success = await onAddFilm(values);
    if (success) {
      form.reset();
      setDialogOpen(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-4">
      <div className={`flex ${isMobile ? 'flex-col gap-3 w-full' : 'flex-row items-center gap-4 w-full'}`}>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('search_films')}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className={`flex ${isMobile ? 'flex-row justify-between items-center' : 'flex-row items-center'} gap-3`}>
          <SortButton 
            options={sortOptions}
            activeSort={activeSort}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
            onDirectionChange={onDirectionChange}
          />

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className={`whitespace-nowrap ${isMobile ? 'h-9' : ''}`}>
                <PlusCircle className="h-4 w-4 mr-1 sm:mr-2" />
                {t('add_film')}
              </Button>
            </DialogTrigger>
            <DialogContent className={isMobile ? "w-[95vw] max-w-[95vw] p-4" : "max-w-md"}>
              <DialogHeader className="mb-1">
                <DialogTitle>{t('add_new_film')}</DialogTitle>
                <DialogDescription className="text-xs">
                  {t('add_film_details')}
                </DialogDescription>
              </DialogHeader>
              <AddFilmForm form={form} onSubmit={handleAddFilm} />
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
